import React, { useEffect, useRef, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useHistory, useLocation } from 'react-router-dom';

export function isMobileView() {
  return useMediaQuery({ maxWidth: 765 });
}

export function isTabletView() {
  return useMediaQuery({ minWidth: 766, maxWidth: 1024 });
}

export function isLaptopView() {
  return useMediaQuery({ minWidth: 1025, maxWidth: 1440 });
}

export function isDesktopView() {
  return useMediaQuery({ minWidth: 1441 });
}

export function isSmallScreen() {
  return useMediaQuery({ maxWidth: 1024 });
}

/**
 * A touch device held sideways.
 *
 * This is the mirror of isMobileView() above, not a separate policy. In
 * portrait a device's width IS its short side, so `max-width: 765px` already
 * picks the surface by short side; `max-height: 765px` in landscape asks the
 * same question of the same number. The invariant that falls out is the one
 * that matters: for any touch device the surface depends only on
 * min(width, height), so rotating the device cannot move it to a surface built
 * for another form factor. An iPhone is mobile both ways; an iPad (768px short
 * side) is desktop both ways; an iPad mini (744px short side) is mobile both
 * ways instead of flipping on rotation as it used to.
 *
 * Width alone cannot express this: a landscape iPhone is 844-932px wide, i.e.
 * indistinguishable from a desktop browser window someone dragged narrow. The
 * distinguishing signal is the PRIMARY pointer — `pointer: coarse` is
 * touch-first hardware, and a mouse-driven desktop (or a touchscreen laptop,
 * whose primary pointer is still the mouse) never matches. Desktop windows keep
 * their existing width-only behaviour because they never satisfy this clause.
 *
 * Deliberately UNBOUNDED in width. Any max-width would break the mirror: an
 * iPad mini is 744x1133, so it is 1133px wide once rotated, and a 1024px cap
 * would put one device on two surfaces — the defect itself. Height does all the
 * separating, and a short side over 765px is desktop in both orientations.
 *
 * Deliberately NO `(hover: none)`. It describes the same primary input
 * mechanism `pointer` does, so it excludes nothing extra here — a mouse desktop
 * is already out on `pointer: coarse`. All it adds is false-negative surface:
 * any device reporting coarse alongside hover:hover would fall out of the
 * clause and get the desktop app in a 390px-tall window, which is the exact bug
 * this clause exists to prevent.
 *
 * Written as a raw query string on purpose: react-responsive@9 has no `pointer`
 * matcher, and toQuery() emits only the keys in its own `mediaQuery.all` map,
 * so `{ pointer: 'coarse' }` is dropped and the clause degrades SILENTLY to a
 * size-only predicate — which would match that narrow desktop window. (An
 * object holding nothing but pointer throws instead, on the empty query it
 * produces; the realistic mixed object fails quietly, which is the worse half.)
 */
export const LANDSCAPE_TOUCH_QUERY =
  '(orientation: landscape) and (max-height: 765px) and (pointer: coarse)';

export function isLandscapeTouchView() {
  return useMediaQuery({ query: LANDSCAPE_TOUCH_QUERY });
}

const normalisePath = (path) => {
  const value = String(path || '/').replace(/^#/, '');
  const pathname = value.split(/[?#]/)[0] || '/';
  return pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
};

/**
 * Routes whose editor tree owns unsaved, local form state.
 *
 * Swapping the whole app while one of these screens is open destroys that
 * state. Keep the tree that opened the workflow mounted across breakpoint
 * changes, then release it as soon as the workflow leaves the write routes.
 */
export function isMobileWriteWorkflowPath(path) {
  const pathname = normalisePath(path);
  return [
    /^\/tasks\/new\/?$/,
    /^\/tasks\/[^/]+\/edit\/?$/,
    /^\/tasks\/[^/]+\/?$/,
    /^\/m\/new-draft\/?$/,
    /^\/tools\/(?:rubrics|markingcriterias|commentbanks|successcriterias|shortanswers)\/new\/?$/,
    /^\/tools\/(?:rubrics|markingcriterias|commentbanks|successcriterias|shortanswers)\/[^/]+\/?$/,
    /^\/tools\/(?:advanced-controls|advanced-marking)\/?$/,
    /^\/advanced-marking\/?$/,
    /^\/documents\/[^/]+\/?$/,
    /^\/submissions\/[^/]+(?:\/(?:revise|review))?\/?$/,
    /^\/view\/submissions\/[^/]+\/?$/,
    /^\/(?:feedbackRequest\/)?documentsReview\/[^/]+\/?$/,
    /^\/(?:m\/account|account|settings)\/?$/,
  ].some((pattern) => pattern.test(pathname));
}

const currentHashPath = () => {
  if (typeof window === 'undefined') return '/';
  return normalisePath(window.location.hash || window.location.pathname);
};

/**
 * App-level mobile decision with a lossless handoff for active write flows.
 * Other consumers should keep using isMobileView(), which remains the literal
 * media-query result.
 */
export function useMobileHandoffView(mobileExperienceEnabled = true) {
  const narrowView = isMobileView();
  const landscapeTouch = isLandscapeTouchView();
  // A rotated device is the same device. The landscape branch is flag-gated so
  // a flag-off build behaves exactly as it does today: with the mobile
  // experience dark, a landscape phone would otherwise be pushed off the
  // cramped-but-usable desktop app onto the "open this on desktop" wall
  // AppMobileGate renders.
  const physicalMobileView =
    narrowView || (mobileExperienceEnabled && landscapeTouch);
  const [, refreshRoute] = useState(0);
  const workflowMobileView = useRef(null);

  useEffect(() => {
    const syncPath = () => refreshRoute((version) => version + 1);
    window.addEventListener('hashchange', syncPath);
    window.addEventListener('popstate', syncPath);
    return () => {
      window.removeEventListener('hashchange', syncPath);
      window.removeEventListener('popstate', syncPath);
    };
  }, []);

  // Read from the address bar on every render as well as listening below. If a
  // resize lands in the same frame as a route push, the breakpoint decision
  // still sees the new write route instead of one stale state value.
  const path = currentHashPath();
  const isWriteWorkflow = isMobileWriteWorkflowPath(path);

  // Capture the owning tree before rendering either branch. Protect both
  // directions: a transient mobile match must not unmount a desktop draft and
  // then latch it into mobile when the desktop media queries recover. Waiting
  // for an effect would already have destroyed the outgoing editor's state.
  if (!mobileExperienceEnabled || !isWriteWorkflow) {
    workflowMobileView.current = null;
  } else if (workflowMobileView.current === null) {
    workflowMobileView.current = physicalMobileView;
  }

  return workflowMobileView.current ?? physicalMobileView;
}

/**
 * Translate URLs that exist only in MobileApp before the desktop tree takes
 * over. Shared routes deliberately return null and keep their normal desktop
 * behavior.
 */
export function desktopPathForMobileRoute(path) {
  const pathname = normalisePath(path);
  let match;

  if (
    pathname === '/m/new-draft' ||
    pathname === '/m/samples' ||
    pathname.startsWith('/m/samples/')
  ) {
    return '/getFeedback';
  }
  if (pathname === '/settings') {
    return '/tools/rubrics';
  }
  if (
    pathname === '/m/more' ||
    pathname === '/m/notifications' ||
    pathname === '/m/account' ||
    pathname === '/account'
  ) {
    return '/';
  }
  if ((match = pathname.match(/^\/tasks\/([^/]+)\/edit\/?$/))) {
    return `/tasks/${match[1]}`;
  }
  if ((match = pathname.match(/^\/submissions\/([^/]+)\/revise\/?$/))) {
    return `/submissions/${match[1]}`;
  }
  if (
    (match = pathname.match(/^\/tools\/markingcriterias(?:\/([^/]+))?\/?$/))
  ) {
    return match[1] ? `/tools/rubrics/${match[1]}` : '/tools/rubrics';
  }
  if (pathname === '/m' || pathname.startsWith('/m/')) return '/';
  return null;
}

/** Replace a mobile-only URL once the desktop tree owns the viewport. */
export function DesktopRouteCanonicalizer({ mobileView }) {
  const location = useLocation();
  const history = useHistory();

  useEffect(() => {
    if (mobileView) return;
    const desktopPath = desktopPathForMobileRoute(location.pathname);
    if (!desktopPath || desktopPath === location.pathname) return;
    history.replace({
      pathname: desktopPath,
      search: location.search,
    });
  }, [history, location.pathname, location.search, mobileView]);

  return null;
}

export default function ReactiveRender(props) {
  const { mobile, tablet, laptop, desktop } = props;
  const mobileView = isMobileView();
  const tabletView = isTabletView();
  const laptopView = isLaptopView();
  const desktopView = isDesktopView();

  return (
    <>
      {mobileView && mobile}
      {tabletView && tablet}
      {laptopView && laptop}
      {desktopView && desktop}
    </>
  );
}
